import { msg } from '@lingui/core/macro';
import type { MessageDescriptor } from 'shared/i18n';
import { TemplateType } from '../template-type';
import { PlaceholderKey } from './placeholder-keys';
import type { PlaceholderData, PlaceholderKind } from './types';

const MULTI_MATCH_TEMPLATE_TYPES: ReadonlySet<TemplateType> = new Set([
  TemplateType.MULTIPLE_SCHEDULE,
  TemplateType.MULTIPLE_RESULTS,
]);

/**
 * Returns true when the active template type renders several matches at once
 * and the given key resolves against per-match data. Used by the inspector to
 * show the `Match #` field and red-highlight a missing value.
 *
 * Keys that come from per-match data are anything that the resolver routes
 * through a match slot. In practice that's every SCHEDULE/RESULTS key except
 * pure tournament/championship metadata.
 */
const MATCH_SCOPED_KEYS: ReadonlySet<string> = new Set<string>([
  PlaceholderKey.TEAM_NAME,
  PlaceholderKey.TEAM_SHORT_NAME,
  PlaceholderKey.TEAM_LOGO,
  PlaceholderKey.SCORE,
  PlaceholderKey.SCORE_TEAM,
  PlaceholderKey.MATCH_DATE,
  PlaceholderKey.MATCH_TIME,
  PlaceholderKey.MATCH_VENUE,
  PlaceholderKey.ROUND_NAME,
]);

export function isMultiMatchTemplateType(type: TemplateType | undefined): boolean {
  return type !== undefined && MULTI_MATCH_TEMPLATE_TYPES.has(type);
}

export function needsMatchNumber(key: string | undefined, type: TemplateType | undefined): boolean {
  if (!isMultiMatchTemplateType(type)) return false;
  if (!key) return false;
  return MATCH_SCOPED_KEYS.has(key);
}

type MissingFieldsFn = (data: PlaceholderData, templateType?: TemplateType) => readonly string[];

type RegistryEntry = {
  /**
   * Inspector label. A descriptor, not a string: the registry is module-level, so
   * the language is not known yet — translate at the call site with `t(label)`.
   * The kit `.claude/skills/media-manager-templates` reads `label.message` (the
   * Russian source) through its own `msg` shim.
   */
  label: MessageDescriptor;
  output: 'text' | 'image';
  missingFields: MissingFieldsFn;
};

const isPlayerStats = (t: TemplateType | undefined): boolean =>
  t === TemplateType.TOP_PLAYERS_BY_INDICATORS;

/**
 * Team keys. Normally the team is addressed directly by `teamNumber` (home/away
 * on a match, or a standings row). On a player-statistics template there is no
 * team axis at all: the team is whichever team the ranked player in slot
 * `playerNumber` plays for, so the same key reads a different identity field.
 */
const teamMissing: MissingFieldsFn = (d, t) =>
  isPlayerStats(t)
    ? d.playerNumber
      ? []
      : ['playerNumber']
    : d.teamNumber
      ? []
      : ['teamNumber'];

/**
 * Player keys. Normally a player is (team, index-into-that-team's-roster). On a
 * player-statistics template the rank slot alone identifies the player.
 */
const playerMissing: MissingFieldsFn = (d, t) => {
  if (isPlayerStats(t)) return d.playerNumber ? [] : ['playerNumber'];
  const out: string[] = [];
  if (!d.teamNumber) out.push('teamNumber');
  if (!d.playerNumber) out.push('playerNumber');
  return out;
};

/**
 * Per-championship player stats. Only ever allowed on a player-statistics
 * template, so the rank slot is unconditionally the only identity they read.
 */
const rankMissing: MissingFieldsFn = (d) => (d.playerNumber ? [] : ['playerNumber']);

const none = () => [] as const;

export const PLACEHOLDER_KEY_REGISTRY = {
  [PlaceholderKey.TEAM_NAME]: { label: msg`Название команды`, output: 'text', missingFields: teamMissing },
  [PlaceholderKey.TEAM_SHORT_NAME]: { label: msg`Короткое название команды`, output: 'text', missingFields: teamMissing },
  [PlaceholderKey.TEAM_LOGO]: { label: msg`Логотип команды`, output: 'image', missingFields: teamMissing },
  [PlaceholderKey.SCORE]: { label: msg`Счёт`, output: 'text', missingFields: none },
  [PlaceholderKey.SCORE_TEAM]: { label: msg`Счёт (команда)`, output: 'text', missingFields: teamMissing },
  [PlaceholderKey.CHAMPIONSHIP_NAME]: { label: msg`Название чемпионата`, output: 'text', missingFields: none },
  [PlaceholderKey.TOURNAMENT_NAME]: { label: msg`Название турнира`, output: 'text', missingFields: none },
  [PlaceholderKey.TOURNAMENT_LOGO]: { label: msg`Логотип турнира`, output: 'image', missingFields: none },
  [PlaceholderKey.MATCH_DATE]: { label: msg`Дата матча`, output: 'text', missingFields: none },
  [PlaceholderKey.MATCH_TIME]: { label: msg`Время матча`, output: 'text', missingFields: none },
  [PlaceholderKey.MATCH_VENUE]: { label: msg`Место проведения`, output: 'text', missingFields: none },
  [PlaceholderKey.ROUND_NAME]: { label: msg`Название раунда`, output: 'text', missingFields: none },
  [PlaceholderKey.PLAYER_NAME]: { label: msg`Имя игрока`, output: 'text', missingFields: playerMissing },
  [PlaceholderKey.PLAYER_NUMBER]: { label: msg`Номер игрока`, output: 'text', missingFields: playerMissing },
  [PlaceholderKey.PLAYER_PHOTO]: { label: msg`Фото игрока`, output: 'image', missingFields: playerMissing },
  // Group-level, not row-level: the name comes from the render target itself,
  // so no teamNumber is read. On a non-GROUP stage there is no group at all and
  // the resolver drops the placeholder — that's data availability, not an
  // authoring mistake, so it never reports a missing field.
  [PlaceholderKey.GROUP_NAME]: { label: msg`Название группы`, output: 'text', missingFields: none },
  [PlaceholderKey.TOURNAMENT_POSITION]: { label: msg`Позиция в турнире`, output: 'text', missingFields: teamMissing },
  [PlaceholderKey.GAMES_PLAYED]: { label: msg`Игр сыграно`, output: 'text', missingFields: teamMissing },
  [PlaceholderKey.GAMES_WON]: { label: msg`Игр выиграно`, output: 'text', missingFields: teamMissing },
  [PlaceholderKey.GAMES_LOST]: { label: msg`Игр проиграно`, output: 'text', missingFields: teamMissing },
  [PlaceholderKey.GAMES_DRAWN]: { label: msg`Игр сыграно вничью`, output: 'text', missingFields: teamMissing },
  [PlaceholderKey.GOALS_SCORED]: { label: msg`Голов забито`, output: 'text', missingFields: teamMissing },
  [PlaceholderKey.GOALS_CONCEDED]: { label: msg`Голов пропущено`, output: 'text', missingFields: teamMissing },
  [PlaceholderKey.GOAL_DIFFERENCE]: { label: msg`Разница мячей`, output: 'text', missingFields: teamMissing },
  [PlaceholderKey.GAMES_DIFFERENCE]: { label: msg`Разница игр (выиграно − проиграно)`, output: 'text', missingFields: teamMissing },
  [PlaceholderKey.POINTS]: { label: msg`Очки`, output: 'text', missingFields: teamMissing },
  // Per-championship player statistics. The `(игрок)` suffix disambiguates them
  // from the team-level standing keys above ('Игр сыграно', 'Голов забито', …),
  // following SCORE_TEAM's 'Счёт (команда)' precedent. The two sets can never
  // appear in the same dropdown via `allowedPlaceholderKeys` — but the inspector
  // falls back to every kind-compatible key while `templateType` is still
  // undefined (placeholder-identity-section.tsx), and in that window both are
  // listed. Labels must therefore be globally unique; a test asserts it.
  [PlaceholderKey.PLAYER_POSITION]: { label: msg`Амплуа игрока`, output: 'text', missingFields: rankMissing },
  [PlaceholderKey.PLAYER_GAMES_PLAYED]: { label: msg`Игр сыграно (игрок)`, output: 'text', missingFields: rankMissing },
  [PlaceholderKey.PLAYER_GOALS]: { label: msg`Голов забито (игрок)`, output: 'text', missingFields: rankMissing },
  [PlaceholderKey.PLAYER_ASSISTS]: { label: msg`Голевых передач (игрок)`, output: 'text', missingFields: rankMissing },
  [PlaceholderKey.PLAYER_GOALS_ASSISTS]: { label: msg`Голы + передачи (игрок)`, output: 'text', missingFields: rankMissing },
  [PlaceholderKey.PLAYER_YELLOW_CARDS]: { label: msg`Жёлтых карточек (игрок)`, output: 'text', missingFields: rankMissing },
  [PlaceholderKey.PLAYER_RED_CARDS]: { label: msg`Красных карточек (игрок)`, output: 'text', missingFields: rankMissing },
  // One placeholder per team: own goals are listed under the team they were
  // credited to, so the team axis is mandatory. Not in MATCH_SCOPED_KEYS — the
  // key is allowed on the single-match RESULTS template only.
  [PlaceholderKey.GOAL_SCORERS]: { label: msg`Авторы голов`, output: 'text', missingFields: teamMissing },
} satisfies Record<PlaceholderKey, RegistryEntry>;

export function getRegistryEntry(key: string): RegistryEntry | undefined {
  return (PLACEHOLDER_KEY_REGISTRY as Record<string, RegistryEntry>)[key];
}

const expectedOutputForKind = (kind: PlaceholderKind | undefined): 'text' | 'image' | undefined => {
  if (kind === 'placeholder-image') return 'image';
  if (kind === 'placeholder-text') return 'text';
  return undefined;
};

export function isKeyCompatibleWithKind(key: string, kind: PlaceholderKind | undefined): boolean {
  const entry = getRegistryEntry(key);
  const expected = expectedOutputForKind(kind);
  if (!entry || !expected) return false;
  return entry.output === expected;
}

export function getKeysForKind(kind: PlaceholderKind): readonly PlaceholderKey[] {
  const expected = expectedOutputForKind(kind);
  if (!expected) return [];
  return (Object.values(PlaceholderKey) as PlaceholderKey[]).filter((k) => PLACEHOLDER_KEY_REGISTRY[k].output === expected);
}

const EMPTY_IDENTITY = { teamNumber: undefined, playerNumber: undefined } as PlaceholderData;

/**
 * Which of the team#/player# identity fields a key actually reads on the active
 * template type. Reuses each entry's `missingFields` predicate against an
 * all-empty identity — whatever it reports missing is exactly what it checks,
 * with no separate "which fields does this key use" table to keep in sync.
 *
 * `templateType` is required (unlike `missingFieldsFor`'s optional one): the
 * answer genuinely differs per type — TEAM_NAME reads `teamNumber` on a match
 * template and `playerNumber` on a player-statistics one — so a caller that
 * omitted it would silently render the wrong inspector fields.
 *
 * Unknown/unset keys report every field the type has an axis for — the
 * inspector can't yet tell what the author is about to pick, so it shows
 * everything rather than guessing. A player-statistics template has no team
 * axis, so it never offers `teamNumber` even in that fallback.
 */
export function usedIdentityFieldsFor(
  key: string | undefined,
  templateType: TemplateType | undefined,
): ReadonlySet<'teamNumber' | 'playerNumber'> {
  const entry = key ? getRegistryEntry(key) : undefined;
  if (!entry) {
    return isPlayerStats(templateType)
      ? new Set<'teamNumber' | 'playerNumber'>(['playerNumber'])
      : new Set<'teamNumber' | 'playerNumber'>(['teamNumber', 'playerNumber']);
  }
  const fields = entry.missingFields(EMPTY_IDENTITY, templateType);
  return new Set(fields.filter((f): f is 'teamNumber' | 'playerNumber' => f === 'teamNumber' || f === 'playerNumber'));
}

export function missingFieldsFor(
  data: PlaceholderData,
  templateType?: TemplateType,
): readonly string[] {
  const entry = getRegistryEntry(data.placeholderKey);
  if (!entry) return ['placeholderKey'];
  const expected = expectedOutputForKind(data.kind);
  if (expected && entry.output !== expected) return ['placeholderKey'];
  const base = entry.missingFields(data, templateType);
  if (needsMatchNumber(data.placeholderKey, templateType) && !data.matchNumber) {
    return [...base, 'matchNumber'];
  }
  return base;
}
